import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { HeroSection } from './components/HeroSection';
import { InputForm } from './components/InputForm';
import { ResultsSection } from './components/ResultsSection';
import { HowItWorks } from './components/HowItWorks';
import { Footer } from '../../shared/components/Footer';
import { toast } from 'sonner';
import { generateHeadlines } from './utils/headlineGenerator';
import { useAuth } from '../../contexts/AuthContext';
import { AuthModal, AuthButton, CreditConfirmModal } from '../../components/auth';

const HeadlineGeneratorTool = () => {
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState('login');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  
  const { isAuthenticated, credits, refreshUserProfile } = useAuth();

  const handleGenerate = async (formData) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setPendingFormData(formData);
      setAuthModalView('login');
      setShowAuthModal(true);
      return;
    }
    
    // Check if user has credits
    if (credits < 1) {
      toast.error('You have no credits left. Please upgrade to continue.');
      return;
    }
    
    // Show credit confirmation modal
    setPendingFormData(formData);
    setShowCreditModal(true);
  };
  
  const executeGeneration = async (formData) => {
    setIsLoading(true);
    setResults(null);
    
    try {
      const generatedResults = await generateHeadlines(formData);
      setResults(generatedResults);
      
      // Refresh user profile to update credits display
      await refreshUserProfile();
      
      // Scroll to results
      setTimeout(() => {
        document.querySelector('#results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error generating headlines:', error);
      
      if (error.message === 'AUTH_REQUIRED') {
        setPendingFormData(formData);
        setAuthModalView('login');
        setShowAuthModal(true);
        return;
      }
      
      if (error.message === 'INSUFFICIENT_CREDITS') {
        toast.error('You have no credits left. Please upgrade to continue.');
        return;
      }
      
      toast.error(error.message || 'Failed to generate headlines. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreditConfirm = () => {
    setShowCreditModal(false);
    if (pendingFormData) {
      executeGeneration(pendingFormData);
      setPendingFormData(null);
    }
  };
  
  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // If we had pending form data, show credit confirmation
    if (pendingFormData) {
      setShowCreditModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Tool Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link 
                to="/" 
                className="flex items-center gap-1 sm:gap-2 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium hidden xs:inline">All Tools</span>
              </Link>
              <div className="h-6 w-px bg-border flex-shrink-0 hidden sm:block" />
              <a href="https://growleads.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90 transition-opacity min-w-0">
                <img 
                  src="/headline-tool-tans.png" 
                  alt="Growleads.io Logo" 
                  className="h-8 w-8 sm:h-10 sm:w-auto object-contain flex-shrink-0"
                />
                <span className="font-display font-semibold text-foreground text-sm sm:text-lg truncate hidden sm:block">LinkedIn Headline Generator</span>
              </a>
            </div>
            <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="hidden md:flex items-center gap-6">
                <a href="#generator" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Generator</a>
                <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
              </div>
              <AuthButton />
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <HeroSection />
        <HowItWorks />
        <InputForm onGenerate={handleGenerate} isLoading={isLoading} />
        {results && (
          <div id="results">
            <ResultsSection results={results} />
          </div>
        )}
      </main>
      <Footer />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => {
          setShowAuthModal(false);
          setPendingFormData(null);
        }}
        defaultView={authModalView}
      />
      
      {/* Credit Confirmation Modal */}
      <CreditConfirmModal
        isOpen={showCreditModal}
        onClose={() => {
          setShowCreditModal(false);
          setPendingFormData(null);
        }}
        onConfirm={handleCreditConfirm}
        credits={1}
        toolName="Headline Generator"
        currentCredits={credits}
      />
    </div>
  );
};

export default HeadlineGeneratorTool;
